import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { SocialMediaIcon, type SocialMediaIconProps } from '@kontentino/ui';
import ReportComparisonBadge from '../primitives/ReportComparisonBadge';
import { CHART_COLORS } from 'app/modules/analytics/components/shared/charts/chartConfig';
import NumberUtil from 'utils/number';
import type { OverviewResponse } from 'api/new-analytics/types';

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
};

interface Props {
  data: OverviewResponse;
}

const ReportPreviewOverviewChannels = ({ data }: Props) => {
  const { t } = useTranslation();

  const reach = data.summary?.totals?.reach;
  const engagement = data.summary?.totals?.engagement;

  const platformItems = useMemo(
    () => (data.highlights?.per_platform ?? []).filter((p) => p.value != null),
    [data.highlights?.per_platform],
  );
  const maxValue = useMemo(
    () => Math.max(...platformItems.map((p) => p.value), 1),
    [platformItems],
  );

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-3 tw-overflow-hidden tw-rounded-xl tw-border tw-border-grayscale-10 tw-bg-white tw-p-3 tw-shadow-sm"
      data-cy="analytics-overview-export_channels-card"
    >
      <span className="tw-text-base tw-font-semibold tw-text-grayscale-180">
        {t('channels', { defaultValue: 'Channels' })}
      </span>

      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {reach ? (
          <div className="tw-inline-flex tw-flex-col tw-items-start tw-rounded-lg tw-border tw-border-grayscale-10 tw-bg-grayscale-5 tw-px-[13px] tw-py-[9px]">
            <span className="tw-text-xs tw-font-medium tw-text-grayscale-180">
              {t('reach', { defaultValue: 'Reach' })}
            </span>
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-xl tw-font-semibold tw-text-grayscale-180">
                {NumberUtil.compact(reach.value)}
              </span>
              {reach.change_percent != null && (
                <ReportComparisonBadge percentage={reach.change_percent} />
              )}
            </div>
          </div>
        ) : null}

        {engagement ? (
          <div className="tw-inline-flex tw-flex-col tw-items-start tw-rounded-lg tw-border tw-border-grayscale-10 tw-bg-grayscale-5 tw-px-[13px] tw-py-[9px]">
            <span className="tw-text-xs tw-font-medium tw-text-grayscale-180">
              {t('engagement', { defaultValue: 'Engagement' })}
            </span>
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-xl tw-font-semibold tw-text-grayscale-180">
                {NumberUtil.compact(engagement.value)}
              </span>
              {engagement.change_percent != null && (
                <ReportComparisonBadge percentage={engagement.change_percent} />
              )}
            </div>
          </div>
        ) : null}
      </div>

      {platformItems.length > 0 ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {platformItems.map((item) => {
            const pct = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            const positive =
              item.change_percent != null && item.change_percent > 0;
            const negative =
              item.change_percent != null && item.change_percent < 0;

            return (
              <div
                key={item.platform}
                className="tw-flex tw-items-center tw-gap-4"
              >
                <div className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center">
                  <SocialMediaIcon
                    size={20}
                    type={item.platform as SocialMediaIconProps['type']}
                  />
                </div>
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                  <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-medium tw-text-grayscale-180">
                    <span>
                      {PLATFORM_LABELS[item.platform] ?? item.platform}
                    </span>
                    <div className="tw-flex tw-gap-2">
                      <span>{NumberUtil.compact(item.value)}</span>
                      {item.change_percent != null && (
                        <span
                          className={clsx(
                            positive && 'tw-text-success-100',
                            negative && 'tw-text-danger-100',
                            !positive && !negative && 'tw-text-grayscale-100',
                          )}
                        >
                          {positive ? '+' : ''}
                          {Math.round(item.change_percent)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className="tw-relative tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-grayscale-10"
                    style={{ height: 6 }}
                  >
                    <div
                      className="tw-absolute tw-left-0 tw-top-0 tw-rounded-full"
                      style={{
                        height: 6,
                        width: `${pct}%`,
                        backgroundColor: CHART_COLORS.defaultDark,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default ReportPreviewOverviewChannels;
